import { mdiCalendarCheckOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState, useRef, Fragment } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  fetchTimeOffHistory,
  fetchVacationExtraOrUsedBalance,
} from '../../../store/apis/leaveService';
import { VACATION_LEAVES_TYPES } from './constants';

import './TimeOffWidget.scss';
import TimeOffModal from './TimeOffModal';

function TimeOffWidget() {
  const containerRef = useRef(null);
  const [isModelOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: 'Modal Title',
    dataConfig: null,
    data: [],
  });
  const { data: timeOffHistoryData } = useQuery({
    queryKey: ['timeOffHistory'],
    queryFn: () => fetchTimeOffHistory({}),
    keepPreviousData: true,
    enabled: true,
  });

  const generateDataConfig = (data = [], numericKeys = []) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const keys = Object.keys(data[0]);
    return keys.map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      render: numericKeys.includes(key)
        ? row => parseFloat(row[key]).toFixed(1)
        : undefined,
    }));
  };

  const { mutate: vacationExtraOrUsedBalanceMutation } = useMutation({
    mutationFn: vacationParams =>
      fetchVacationExtraOrUsedBalance({
        type: vacationParams,
      }),
    onSuccess: (dataResponse, params) => {
      if (params === VACATION_LEAVES_TYPES['Annual Leave']) {
        setModalConfig({
          title: 'Details of Annual Leave',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        });
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Birthday Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Birthday Leave',
          data: dataResponse,
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Casual Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Casual Leave',
          data: dataResponse,
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Hajj Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Hajj Leave',
          data: dataResponse,
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Maternity/Paternity Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Maternity/Paternity Leave',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Sick Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Sick Leave',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Study Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Study Leave',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Unpaid Leave']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Unpaid Leave',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        }));
        setIsModalOpen(true);
      }
      if (params === VACATION_LEAVES_TYPES['Other Time off']) {
        setModalConfig(prev => ({
          ...prev,
          title: 'Details of Other Time off',
          data: dataResponse,
          dataConfig: generateDataConfig(dataResponse, ['Days']),
        }));
        setIsModalOpen(true);
      }
    },
  });

  const handleVacationTypeModalClick = type => {
    if (!type) return;
    vacationExtraOrUsedBalanceMutation(type);
  };

  const getTitle = type => {
    switch (type) {
      case 'Annual Leave':
        return 'Number of annual leave days taken.';
      case 'Maternity/Paternity Leave':
        return 'Number of maternity/paternity leave days taken.';
      case 'Sick Leave':
        return 'Number of sick leave days taken.';
      case 'Birthday Leave':
        return 'Number of birthday leave days taken.';
      case 'Hajj Leave':
        return 'Number of Hajj leave days taken.';
      case 'Unpaid Leave':
        return 'Number of unpaid leave days taken.';
      case 'Other Time off':
        return 'Number of days taken under other leave categories.';
      case 'Casual Leave':
        return 'Number of casual leave days taken.';
      case 'Study Leave':
        return 'Number of study leave days taken.';
      default:
        return null;
    }
  };

  const renderBorder = index => {
    const isLastItem = index === timeOffHistoryData.length - 1;
    if (isLastItem) {
      return null;
    }
    return <div className="header-bottom-border" />;
  };

  return (
    <Card className="time-off-widget">
      <CardHeader className="time-off-widget_header">
        <div className="d-flex flex-row align-items-center gap-2 pb-0">
          <div>
            <Icon path={mdiCalendarCheckOutline} size="20px" color="#363F72" />
          </div>
          <span className="fs-5">Time Off History</span>
        </div>
      </CardHeader>
      <CardBody
        ref={containerRef}
        className="px-3 py-0"
        style={{ height: '100%', overflowY: 'auto' }}
      >
        <div className="time-off-body">
          <div className="header-bottom-border" />
          {timeOffHistoryData?.map((item, index) => {
            const [value, labelPart] = item.TIMEOFF.split(':');
            const label = labelPart.replace('days of ', '').trim();
            return (
              <Fragment key={index}>
                <div
                  title={getTitle(label)}
                  className="vacation-type"
                  style={
                    VACATION_LEAVES_TYPES[label]
                      ? { cursor: 'pointer' }
                      : { cursor: 'default' }
                  }
                  onClick={() =>
                    handleVacationTypeModalClick(VACATION_LEAVES_TYPES[label])
                  }
                >
                  <span className="vacation-type-label">{label}</span>
                  <div className="d-flex flex-row align-items-center gap-2">
                    <span
                      style={{
                        color: '#026aa2',
                        textDecoration: VACATION_LEAVES_TYPES[label],
                        fontWeight: 700,
                      }}
                    >
                      {parseFloat(value.trim()).toFixed(1)}
                    </span>
                  </div>
                </div>
                {renderBorder(index)}
              </Fragment>
            );
          })}
        </div>
      </CardBody>
      <TimeOffModal
        isOpen={isModelOpen}
        toggle={() => setIsModalOpen(!isModelOpen)}
        config={modalConfig}
      />
    </Card>
  );
}

export default TimeOffWidget;
